import dayjs from 'utils/dayjs';
import { get } from 'lodash';
import { buildObjectFromKeys, sumObjectsProperties } from 'utils/objects';
import { ROOM_KEYS, REVENUE_KEYS, BUDGET_KEYS, INVENTORY_KEYS, STATISTIC_KEYS } from 'utils/analytics/types';
import {
    calculateMetrics,
    calculateBudgetVariances,
    calculateAdr,
    calculateRevPar,
    calculateOccupancy,
} from 'utils/analytics/metrics';
import { roundCurrency, roundPercentage } from 'utils/analytics/numbers';
let utils;

const INVENTORY_BLOCKS_KEYS = ['inventoryBlocks.rooms', 'inventoryBlocks.revenue'];

/**
 * Sum figures in all rows & return the totals
 * Only sums figures that should be cumulated (room, revenue, budget, inventory)
 * @param  {Object[]} rows - rows to sum
 * @return {Object}   totals
 */
const sumAllRows = (rows) => {
    const keysToSum = [
        ...ROOM_KEYS,
        ...REVENUE_KEYS,
        ...INVENTORY_KEYS,
        ...BUDGET_KEYS,
        ...STATISTIC_KEYS,
        ...INVENTORY_BLOCKS_KEYS,
    ];
    const initialInvBlock = {
        rooms: 0,
        revenue: null,
    };
    const initialBudget = buildObjectFromKeys(BUDGET_KEYS, null);
    const initialStats = buildObjectFromKeys(STATISTIC_KEYS, null);
    // Other figures use 0 by default, so no need to define initial object

    const cumulatedTotals = rows.reduce(
        (totals, row) => {
            const forecast = {
                rooms: row.forecast.rooms == null ? row.rooms.guaranteed : row.forecast.rooms,
                revenue: row.forecast.revenue == null ? row.revenue.accommodation.guaranteed : row.forecast.revenue,
                adr: row.forecast.adr == null ? row.adr : row.forecast.adr,
                revPar: row.forecast.revPar == null ? row.revPar : row.forecast.revPar,
                occupancy: row.forecast.occupancy == null ? row.occupancy : row.forecast.occupancy,
            };

            return {
                ...sumObjectsProperties(totals, row, keysToSum),
                forecast: {
                    rooms: totals.forecast.rooms + forecast.rooms,
                    revenue: totals.forecast.revenue + forecast.revenue,
                    adr: totals.forecast.adr + forecast.adr,
                    revPar: totals.forecast.revPar + forecast.revPar,
                    occupancy: totals.forecast.occupancy + forecast.occupancy,
                },
            };
        },
        {
            ...initialBudget,
            ...initialStats,
            ...initialInvBlock,
            forecast: { rooms: 0, revenue: 0, adr: 0, revPar: 0, occupancy: 0 },
        },
    );

    // ADR / RevPar / Occupancy budgets - average
    const averageValue = (dividend, divisor) => (!Number.isNaN(divisor) && divisor > 0 ? dividend / divisor : null);

    // budget
    const averageAdrBudget = averageValue(cumulatedTotals.budget.revenue.accommodation, cumulatedTotals.budget.rooms);
    const averageRevParBudget = averageValue(
        cumulatedTotals.budget.revenue.accommodation,
        cumulatedTotals.inventory.availableRoomCount,
    );
    const averageOccupancyBudget = averageValue(
        cumulatedTotals.budget.rooms,
        cumulatedTotals.inventory.availableRoomCount,
    );
    cumulatedTotals.budget.adr = averageAdrBudget !== null ? roundCurrency(averageAdrBudget) : null;
    cumulatedTotals.budget.revPar = averageRevParBudget !== null ? roundCurrency(averageRevParBudget) : null;
    cumulatedTotals.budget.occupancy = averageOccupancyBudget !== null ? roundPercentage(averageOccupancyBudget) : null;

    // forecast
    const averageAdrForecast = averageValue(cumulatedTotals.forecast.revenue, cumulatedTotals.forecast.rooms);
    const averageRevParForecast = averageValue(
        cumulatedTotals.forecast.revenue,
        cumulatedTotals.inventory.availableRoomCount,
    );
    const averageOccupancyForecast = averageValue(
        cumulatedTotals.forecast.rooms,
        cumulatedTotals.inventory.availableRoomCount,
    );
    cumulatedTotals.forecast.adr = averageAdrForecast !== null ? roundCurrency(averageAdrForecast) : null;
    cumulatedTotals.forecast.revPar = averageRevParForecast !== null ? roundCurrency(averageRevParForecast) : null;
    cumulatedTotals.forecast.occupancy =
        averageOccupancyForecast !== null ? roundPercentage(averageOccupancyForecast) : null;

    return cumulatedTotals;
};

/**
 * Calculate all the totals for the business review data
 * @param  {Object[]} rows - details per day
 * @return {Object}   totals
 */
const calculateTotalRow = (rows) => {
    // ROOM / REVENUE / BUDGET / INVENTORY / FORECAST / INVENTORY BLOCKS
    const totals = utils.sumAllRows(rows);

    // ADR / REVPAR / OCCUPANCY
    const { adr, revPar, occupancy } = calculateMetrics(
        totals.inventory.availableRoomCount,
        totals.rooms.guaranteed,
        totals.revenue.accommodation.guaranteed,
    );
    Object.assign(totals, { adr, revPar, occupancy });

    // BUDGET VARIANCE
    totals.budgetVariance = calculateBudgetVariances(totals.budget, {
        rooms: totals.rooms.guaranteed,
        revenue: {
            accommodation: totals.revenue.accommodation.guaranteed,
            foodAndBeverage: totals.revenue.foodAndBeverage.guaranteed,
            ancillary: totals.revenue.ancillary.guaranteed,
            other: totals.revenue.other.guaranteed,
        },
        adr,
        revPar,
        occupancy,
    });

    // FORECAST VARIANCE
    totals.forecastVariance = calculateBudgetVariances(
        totals.forecast,
        {
            rooms: totals.rooms.guaranteed,
            revenue: totals.revenue.accommodation.guaranteed,
            adr,
            revPar,
            occupancy,
        },
        ['rooms', 'revenue', 'adr', 'revPar', 'occupancy'],
    );

    return totals;
};

const getRateForDefaultRoomType = (rate, defaultRoomType) => {
    if (!rate) return null;
    const inventoryForDefaultRoomType = rate.inventory.find((row) => row.roomTypeUUID === defaultRoomType.uuid);
    if (inventoryForDefaultRoomType) return inventoryForDefaultRoomType.grossRate;
    return null;
};

/**
 * Parses the BusinessReview data: calculate metrics & totals
 * @param  {Object[]} currentData - current rooms/revenue stats API response
 * @param  {Object[]} budgetData - budget API response
 * @param  {?Object[]} forecastData - forecast API response
 * @param  {?Object[]} statisticsData - statistics API response
 * @param  {?Object[]} sellingRateData
 * @param  {Object} defaultRoomType
 * @param  {Object[]} rates
 * @param  {?Object[]} invBlocks
 * @return {Object[]} data parsed per day
 */
const parseBusinessReviewData = (
    currentData,
    budgetData,
    forecastData,
    statisticsData,
    sellingRateData,
    defaultRoomType = {},
    invBlocks,
) => {
    // Add inventory, metrics & budget variance to each row
    const improvedRows = currentData.map((row) => {
        const { stayDate, ...rowRest } = row;
        // RATE & INV BLOCKS
        const inventoryBlocks = (invBlocks || []).find((invBlock) => invBlock.stayDate === stayDate)
            ?.inventoryBlocks || { rooms: null, revenue: null };
        const sellingRateMatch = (sellingRateData || []).find((sellingRateRow) => sellingRateRow.stayDate === stayDate);
        const sellingRate = getRateForDefaultRoomType(sellingRateMatch, defaultRoomType);

        // ADR / REVPAR / OCCUPANCY
        const { adr, revPar, occupancy } = calculateMetrics(
            row.inventory.availableRoomCount,
            row.rooms.guaranteed,
            row.revenue.accommodation.guaranteed,
        );

        // BUDGET
        const budget = (budgetData || []).find((budgetRow) => budgetRow.stayDate === stayDate) || {
            rooms: null,
            revenue: {
                accommodation: null,
                foodAndBeverage: null,
                ancillary: null,
                other: null,
            },
            adr: null,
            revPar: null,
            occupancy: null,
        };

        // BUDGET VARIANCE
        const budgetVariance = calculateBudgetVariances(budget, {
            rooms: row.rooms.guaranteed,
            revenue: {
                accommodation: row.revenue.accommodation.guaranteed,
                foodAndBeverage: row.revenue.foodAndBeverage.guaranteed,
                ancillary: row.revenue.ancillary.guaranteed,
                other: row.revenue.other.guaranteed,
            },
            adr,
            revPar,
            occupancy,
        });

        // FORECAST
        const forecastMatch = (forecastData || []).find((forecastRow) => forecastRow.stayDate === stayDate) || {
            rooms: null,
            revenue: null,
        };

        const forecastADR =
            forecastMatch.revenue == null || forecastMatch.rooms == null
                ? null
                : calculateAdr(forecastMatch.revenue, forecastMatch.rooms);
        const forecastRevPar =
            forecastMatch.revenue == null || row.inventory.availableRoomCount == null
                ? null
                : calculateRevPar(forecastMatch.revenue, row.inventory.availableRoomCount);
        const forecastOccupancy =
            forecastMatch.rooms == null || row.inventory.availableRoomCount == null
                ? null
                : calculateOccupancy(forecastMatch.rooms, row.inventory.availableRoomCount);

        const forecast = {
            rooms: forecastMatch.rooms,
            revenue: forecastMatch.revenue,
            adr: forecastADR,
            revPar: forecastRevPar,
            occupancy: forecastOccupancy,
        };

        // FORECAST VARIANCE
        const forecastVariance = calculateBudgetVariances(
            forecast,
            {
                rooms: row.rooms.guaranteed,
                revenue: row.revenue.accommodation.guaranteed,
                adr,
                revPar,
                occupancy,
            },
            ['rooms', 'revenue', 'adr', 'revPar', 'occupancy'],
        );

        // STATISTICS
        const statsMatch = statisticsData && statisticsData.find((statsRow) => statsRow.stayDate === stayDate);
        const statistics = {
            rooms: get(statsMatch, 'rooms', null),
            revenue: {
                accommodation: get(statsMatch, 'revenue.accommodation', null),
            },
        };

        return {
            ...rowRest,
            date: dayjs(stayDate, 'YYYY-MM-DD'),
            sellingRate,
            inventoryBlocks,
            budget,
            budgetVariance,
            adr,
            revPar,
            occupancy,
            forecast,
            forecastVariance,
            statistics,
        };
    });

    // Calculate totals
    const totals = utils.calculateTotalRow(improvedRows);

    return {
        rows: improvedRows,
        totalRow: totals,
    };
};

export { parseBusinessReviewData };

export default utils = {
    sumAllRows,
    calculateTotalRow,
};
